import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Link } from "react-router-dom";
import { Menu, X, Ticket } from "lucide-react";
import { BUSINESS } from "./data";

const LINKS = [
  { label: "Sound", href: "#services" },
  { label: "Mixes", href: "#mixes" },
  { label: "Ethos", href: "#manifesto" },
  { label: "Gallery", href: "#gallery" },
  { label: "Contact", href: "#booking" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (lenis) lenis.scrollTo(href, { offset: 0, duration: 1.4 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-ink-950/70 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 md:px-12">
          <button
            data-testid="nav-logo"
            onClick={() => go("#hero")}
            className="group flex items-center gap-3 text-left"
          >
            <span className="h-2 w-2 bg-gold group-hover:rotate-45 transition-transform duration-500" />
            <span className="font-oswald text-lg font-600 uppercase tracking-[0.2em] text-white">
              Nou La Baby<span className="text-gold">.</span>
            </span>
          </button>

          <div className="hidden items-center gap-10 md:flex">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l.href)}
                className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 hover:text-gold transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
            <Link
              to="/events"
              data-testid="nav-tickets"
              className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white hover:text-gold transition-colors duration-300"
            >
              <Ticket size={14} /> Tickets
            </Link>
            <button
              data-testid="nav-book-btn"
              onClick={() => go("#booking")}
              className="group relative overflow-hidden border border-gold/40 px-6 py-2.5"
            >
              <span className="relative z-10 font-mono text-[11px] uppercase tracking-[0.25em] text-gold group-hover:text-ink-950 transition-colors duration-500">
                Book Now
              </span>
              <span className="absolute inset-0 -translate-y-full bg-gold transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-0" />
            </button>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(true)}
            className="text-white md:hidden"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex flex-col bg-ink-950 px-6 py-6"
          >
            <div className="flex items-center justify-between">
              <span className="font-oswald text-lg uppercase tracking-[0.25em]">Jazzy<span className="text-gold">.</span></span>
              <button data-testid="mobile-close" onClick={() => setOpen(false)} aria-label="Close menu">
                <X size={28} className="text-white" />
              </button>
            </div>
            <div className="mt-16 flex flex-col gap-6">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                  onClick={() => go(l.href)}
                  className="font-oswald text-5xl uppercase tracking-tight text-white text-left hover:text-gold transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
              <Link
                to="/events"
                data-testid="mobile-link-tickets"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 font-oswald text-5xl uppercase tracking-tight text-gold text-left"
              >
                <Ticket size={34} /> Tickets
              </Link>
            </div>
            <div className="mt-auto font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
              {BUSINESS.phone}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
